import { Modal } from '@carouselgg/nexus/modal'
import { Button } from '@carouselgg/nexus/button'
import { Text } from '@carouselgg/nexus/text'
import { ChoiceList, Choice } from '@carouselgg/nexus/choice-list'
import { Shield, UserPlus, X } from '@carouselgg/nexus/icon'
import { useAuthStore } from '@carouselgg/auth/store'
import type { DiscoverSeason } from '../../api/compete'
import { freeAgentHint, resumeSignUpHref } from './labels'
import type { RegisterAction } from './labels'
import styles from './RegisterChoiceModal.module.css'

type Season = Pick<DiscoverSeason, 'id' | 'name' | 'free_agent_mode'>

/**
 * "Register to Compete": with a team, or alone as a free agent. Picking one
 * opens that sign-up right here (`onChoose`). A signed-out visitor signs in
 * first and comes back to this page with the same sign-up open.
 *
 * Every competition takes free agents today (free_agent_mode only says how
 * they get placed). If a competition can ever turn them off, that is the one
 * place the second choice should drop out.
 */
export function RegisterChoiceModal({ season, onClose, onChoose }: {
  season: Season | null
  onClose: () => void
  onChoose: (season: Season, mode: RegisterAction) => void
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // Signed in: open the sign-up in place. Signed out: a real link to login,
  // which returns here with the sign-up requested.
  const pick = (mode: RegisterAction) => (season && isAuthenticated
    ? { onClick: () => onChoose(season, mode) }
    : { to: season ? `/login?redirect=${encodeURIComponent(resumeSignUpHref(season, mode))}` : '/login', onClick: onClose })

  return (
    <Modal
      open={season != null}
      onClose={onClose}
      title={season ? `Register for ${season.name}` : ''}
      size="sm"
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X size={16} /> Close
        </Button>
      }
    >
      {season && (
        <div className={styles.body}>
          <ChoiceList aria-label="How do you want to compete?">
            <Choice
              icon={<Shield size={18} />}
              label="With a team"
              hint="Register a team you captain"
              {...pick('register')}
            />
            {season.free_agent_mode && (
              <Choice
                icon={<UserPlus size={18} />}
                label="As a free agent"
                hint={freeAgentHint(season.free_agent_mode)}
                {...pick('free-agent')}
              />
            )}
          </ChoiceList>
          {!isAuthenticated && (
            <Text size="xs" muted>You'll sign in first, then come straight back to finish.</Text>
          )}
        </div>
      )}
    </Modal>
  )
}
